import React, { useState, useEffect } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useLazyQuery } from "@apollo/client";
import { FiInfo, FiExternalLink, FiMusic } from "react-icons/fi";
import { IoCheckmarkSharp } from "react-icons/io5";

import theme from "theme/themeThree";
import { Button } from "MVVM/Components/Button";
import { InputText } from "MVVM/Components/Inputs";
import { STUDIO_QUERY } from "MVVM/GraphQL/studioFacet";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { CompactCardGrid } from "MVVM/Views/Studio/Components/MainRail/Components/CardGrid";
import { ReactComponent as StudioBadgeIcon } from "../../../../assets/images/badges/studio_icon.svg";
import { IAmsCard } from "../@types";

const { REACT_APP_S3_STATIC } = process.env;

interface IFragmovieSettingsProps {
  selectedSong: IAmsCard | null;
  setSelectedSong: (song: IAmsCard | null) => void;
  songVolume: number;
  setSongVolume: (volume: number) => void;
  impactSeverity: number;
  setImpactSeverity: (severity: number) => void;
  speedupVfxEnabled: boolean;
  setSpeedupVfxEnabled: (enabled: boolean) => void;
  applyStudioCards: boolean;
  setApplyStudioCards: (apply: boolean) => void;
  showSongError: boolean;
  setShowSongError: (show: boolean) => void;
}

export const FragmovieSettings = ({
  selectedSong,
  setSelectedSong,
  songVolume,
  setSongVolume,
  impactSeverity,
  setImpactSeverity,
  speedupVfxEnabled,
  setSpeedupVfxEnabled,
  applyStudioCards,
  setApplyStudioCards,
  showSongError,
  setShowSongError,
}: IFragmovieSettingsProps) => {
  // Song selection modal state
  const [songSelectionOpen, setSongSelectionOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [pendingSelection, setPendingSelection] = useState<IAmsCard | null>(
    null,
  );
  const [hoveredSongId, setHoveredSongId] = useState<string | null>(null);

  const [fetchSongs, { data: songsData, loading: songsLoading }] =
    useLazyQuery(STUDIO_QUERY);

  useEffect(() => {
    if (songSelectionOpen) {
      fetchSongs({
        variables: {
          type: ["Music"],
          theme: themeFilter ? [themeFilter] : undefined,
        },
      });
    }
  }, [songSelectionOpen, themeFilter, fetchSongs]);

  const songs: IAmsCard[] = songsData?.facets?.cards || [];
  const themeFacets = songsData?.facets?.facets?.find(
    (f: { identifier: string }) => f.identifier === "theme",
  );

  const filteredSongs = songs.filter((song) =>
    song.ui.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleChooseSong = () => {
    if (pendingSelection) {
      setSelectedSong(pendingSelection);
      setPendingSelection(null);
      setShowSongError(false);
      setSongSelectionOpen(false);
    }
  };

  return (
    <>
      <SettingsContainer>
        <ToggleColumn>
          <ToggleRow>
            <Flex alignItems="center" style={{ gap: 4 }}>
              <Text variant="text.labelSmall">Song Selection</Text>
            </Flex>
            {showSongError && <SongErrorText>required</SongErrorText>}
          </ToggleRow>
          <SongSelectButton
            $highlight={!selectedSong}
            onClick={() => setSongSelectionOpen(true)}
          >
            {selectedSong ? (
              <span>{selectedSong.ui.title}</span>
            ) : (
              <SongPlaceholder>
                <FiMusic size={14} />
                Choose a song...
              </SongPlaceholder>
            )}
          </SongSelectButton>
          {!selectedSong && (
            <SongHint>
              Select a song to configure the rest of your fragmovie
            </SongHint>
          )}
        </ToggleColumn>

        <DisabledOverlay $disabled={!selectedSong}>
          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Impact Intensity</Text>
                <AnimatedTooltip
                  content={
                    <>
                      When the kill and the music align just perfectly...
                      <br />
                      How dramatic should we make the slow motion effect?
                    </>
                  }
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
              </Flex>
              <SettingValue>
                {impactSeverity === 0
                  ? "No impact"
                  : impactSeverity === 100
                  ? "Super slowmo"
                  : `${impactSeverity}%`}
              </SettingValue>
            </ToggleRow>
            <SliderContainer>
              <SliderLabels>
                <span>No impact</span>
                <span>Super slowmo</span>
              </SliderLabels>
              <Slider
                type="range"
                min={0}
                max={100}
                value={impactSeverity}
                onChange={(e) => setImpactSeverity(Number(e.target.value))}
              />
            </SliderContainer>
          </ToggleColumn>

          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Speedup VFX</Text>
                <AnimatedTooltip
                  content="Adds a flash effect synced with the speed of the clip"
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
              </Flex>
              <Checkbox
                $checked={speedupVfxEnabled}
                onClick={() => setSpeedupVfxEnabled(!speedupVfxEnabled)}
              >
                {speedupVfxEnabled && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>

          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <StyledStudioIcon />
                <Text variant="text.labelSmall">Use Studio cards</Text>
                <AnimatedTooltip
                  content={
                    <>
                      Applies your enabled Studio cards (intros, outros,
                      ReShade, watermarks, thumbnails). Music, cameras,
                      transitions, and memes are not applicable.
                    </>
                  }
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
                <GoToStudioLink
                  href="/studio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink size={12} />
                </GoToStudioLink>
              </Flex>
              <Checkbox
                $checked={applyStudioCards}
                onClick={() => setApplyStudioCards(!applyStudioCards)}
              >
                {applyStudioCards && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>
        </DisabledOverlay>
      </SettingsContainer>

      {songSelectionOpen && (
        <SongSelectionOverlay>
          <SongSelectionContainer>
            <SongSelectionHeader>
              <Text variant="text.headingXSmall">Select a Song</Text>
            </SongSelectionHeader>

            <SearchAndFilterRow>
              <SearchInput
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                placeholder="Search songs..."
              />
              {themeFacets && (
                <ThemeFilterSelect
                  value={themeFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setThemeFilter(e.target.value)
                  }
                >
                  <option value="">All Themes</option>
                  {themeFacets.entries?.map(
                    (entry: { label: string; count: number }) => (
                      <option key={entry.label} value={entry.label}>
                        {entry.label} ({entry.count})
                      </option>
                    ),
                  )}
                </ThemeFilterSelect>
              )}
            </SearchAndFilterRow>

            <SongsGridWrapper>
              {songsLoading ? (
                <LoadingContainer>
                  <LoadingSpinner height={30} width={30} />
                </LoadingContainer>
              ) : filteredSongs.length === 0 ? (
                <NoSongsText>No songs found</NoSongsText>
              ) : (
                <CompactCardGrid
                  cards={filteredSongs}
                  selectedCardId={pendingSelection?._id}
                  onCardSelect={(song: IAmsCard) => setPendingSelection(song)}
                  hoveredCardId={hoveredSongId}
                  onCardHover={setHoveredSongId}
                />
              )}
            </SongsGridWrapper>

            <SongSelectionFooter>
              <FooterLeftSection>
                <Button
                  classification="roundedFilledAlt"
                  size="small"
                  onClick={() => {
                    setPendingSelection(null);
                    setSongSelectionOpen(false);
                  }}
                  flex={true}
                >
                  Back
                </Button>
                <SelectionIndicator>
                  {pendingSelection ? (
                    <>
                      Selected:{" "}
                      <SelectedSongTitle>
                        {pendingSelection.ui.title}
                      </SelectedSongTitle>
                    </>
                  ) : (
                    <NoSelectionText>No song selected</NoSelectionText>
                  )}
                </SelectionIndicator>
              </FooterLeftSection>
              <Button
                classification="roundedFilled"
                size="small"
                onClick={handleChooseSong}
                disabled={!pendingSelection}
                flex={true}
              >
                Choose Song
              </Button>
            </SongSelectionFooter>
          </SongSelectionContainer>
        </SongSelectionOverlay>
      )}
    </>
  );
};

// Styled Components
const SettingsContainer = styled(Box)`
  width: 100%;
`;

const DisabledOverlay = styled.div<{ $disabled: boolean }>`
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition: opacity 200ms ease;
`;

const StyledStudioIcon = styled(StudioBadgeIcon)`
  height: 16px;
  width: 16px;
`;

const GoToStudioLink = styled.a`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  margin-left: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CategoryHeader = styled(Text)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 1px;
  padding: 12px 16px 4px;
  text-transform: uppercase;
`;

const ToggleColumn = styled(Flex)`
  align-items: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 16px;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const InfoIcon = styled(FiInfo)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: help;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const SettingValue = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 600;
`;

const SongErrorText = styled(Box)`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 12px;
  font-weight: 500;
`;

const SongSelectButton = styled(Box)<{ $highlight?: boolean }>`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid
    ${({ $highlight, theme }) =>
      $highlight ? theme.colors.envyAlpha40 : theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  margin-top: 8px;
  cursor: pointer;
  font-size: 14px;
  padding: 12px 16px;
  text-align: left;
  width: 100%;

  ${({ $highlight }) =>
    $highlight &&
    `
    animation: songPulse 2s ease-in-out infinite;
  `}

  @keyframes songPulse {
    0%,
    100% {
      border-color: rgba(78, 189, 148, 0.2);
    }
    50% {
      border-color: rgba(78, 189, 148, 0.5);
    }
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    animation: none;
  }
`;

const SongHint = styled(Text)`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 11px;
  margin-top: 6px;
`;

const SliderContainer = styled(Box)`
  margin-top: 8px;
  width: 100%;
`;

const SliderLabels = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  display: flex;
  font-size: 10px;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Slider = styled.input`
  -webkit-appearance: none;
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 2px;
  height: 4px;
  outline: none;
  width: 100%;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    width: 16px;
  }

  &::-moz-range-thumb {
    background: ${({ theme }) => theme.colors.envy};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    width: 16px;
  }
`;

const SongPlaceholder = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  display: flex;
  gap: 8px;
`;

const Checkbox = styled.div<{ $checked: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  transition: all 150ms ease;
  width: 18px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

// Song Selection Styles
const SongSelectionOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const SongSelectionContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  flex-direction: column;
  max-height: 80vh;
  max-width: 600px;
  width: 90%;
`;

const SongSelectionHeader = styled(Box)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: left;
  padding: 24px;
`;

const SearchAndFilterRow = styled(Box)`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
`;

const SearchInput = styled(InputText)`
  ${theme.text.labelSmall}
  background-color: ${({ theme }) => theme.colors.grey825};
  border: none;
  flex: 1;

  ::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const ThemeFilterSelect = styled.select`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }
`;

const SongsGridWrapper = styled(Box)`
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
  padding: 0 24px;

  ::-webkit-scrollbar {
    width: 25px;
  }
  ::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.ned};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.ned};
    background-clip: content-box;
    border: 5px solid transparent;
    border-radius: 999px;
    height: 50px;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey825};
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const NoSongsText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  padding: 40px;
  text-align: center;
`;

const SongSelectionFooter = styled(Box)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
`;

const FooterLeftSection = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const SelectionIndicator = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 13px;
  gap: 4px;
`;

const SelectedSongTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 600;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoSelectionText = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  font-style: italic;
`;
